// Job Analytics
// ----------------------------------------------------------------------------
// Pick a company, search for one or MORE jobs (by number or name) and see the
// analytics for ALL FIVE safety forms — BBS, Safety & Health, Weekly Driver
// Log, Accident/Incident and Trailer Inspection — scoped to each job.
//
// Jobs live in the company-scoped external NTACT jobs API, surfaced through
// /v1/timesheets/get-jobs (the same source the BBS form uses). Flow:
//   1. Company dropdown from /v1/timesheets/company-details.
//   2. Job autocomplete from /v1/timesheets/get-jobs?company_id=&job_num=.
//   3. One /v1/forms/analytics?job_id=&companyid=… feed PER selected job
//      (the endpoint scopes to a single job), fetched in parallel.
//   4. Each job gets a collapsible panel holding all 5 forms' charts (reused
//      from formsanalytics.jsx). Panels open one at a time, accordion style.
//
// WDS note: a weekly driver log spans up to 7 days and each day carries its own
// job, so the server rebuilds `wds_meta` from only the days charged to the
// requested job. Miles/gallons/spend here are per-job, not per-form.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Backdrop, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CallMergeIcon from "@mui/icons-material/CallMerge";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line,
  LineChart,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";

import api from "../../api/api";
import SideNavLayout from "../../Sidenav/SideNavLayout";
import {
  BbsCharts,
  ShpCharts,
  WdsCharts,
  AifCharts,
  TrailerCharts,
  useDrill,
} from "../safetyanalytics/formsanalytics";

// The five safety forms surfaced on this page — each paired with the chart
// component that renders its analytics section (reused from formsanalytics.jsx).
const FORM_TYPES = [
  { code: "BBS", label: "BBS Card", color: "#155DFC", Icon: AssignmentTurnedInIcon, Chart: BbsCharts },
  { code: "SHP", label: "Safety & Health", color: "#00A63E", Icon: HealthAndSafetyIcon, Chart: ShpCharts },
  { code: "WDS", label: "Weekly Driver Log", color: "#E17100", Icon: LocalShippingIcon, Chart: WdsCharts },
  { code: "AIF", label: "Accident / Incident", color: "#E7000B", Icon: WarningAmberIcon, Chart: AifCharts },
  { code: "TRAILER", label: "Trailer Inspection", color: "#7C3AED", Icon: DirectionsBusIcon, Chart: TrailerCharts },
];
const FORM_CODES = FORM_TYPES.map((f) => f.code);

const RANGE_OPTIONS = [
  { key: "all", label: "All Time" },
  { key: "ytd", label: "This Year" },
  { key: "6m", label: "Last 6 Months" },
  { key: "90d", label: "Last 90 Days" },
  { key: "30d", label: "Last 30 Days" },
];

const jobKeyOf = (job) => String(job?.job_num ?? "");

const StatCard = ({ label, value, accent, Icon, onClick }) => {
  const clickable = !!onClick && value > 0;
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={(e) => {
        if (clickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      title={clickable ? `View the ${value} ${label} forms` : undefined}
      className={`flex-1 min-w-[150px] rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition ${
        clickable ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : ""
      }`}
      style={{ border: `2px solid ${accent}` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
          {label}
        </span>
        {Icon && (
          <span
            className="rounded-full p-1.5"
            style={{ background: `${accent}15`, color: accent }}
          >
            <Icon style={{ fontSize: 18 }} />
          </span>
        )}
      </div>
      <div className="mt-2 text-2xl font-bold text-[#0A0A0A] text-left">{value}</div>
    </div>
  );
};

// One collapsible job panel: header always visible, charts mounted only while
// open so five chart sections per job don't all render at once.
const JobPanel = ({ job, data, loading, isOpen, onToggle, onRemove }) => {
  const total = data?.total ?? 0;
  // Card click → the same drill list + form viewer the charts use.
  const { openDrill, node: drillNode } = useDrill([]);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className={`flex items-center gap-3 p-4 cursor-pointer transition ${
          isOpen ? "bg-blue-50/60" : "hover:bg-gray-50"
        }`}
      >
        <span className="rounded-full bg-blue-50 p-2 text-[#155DFC] shrink-0">
          <WorkOutlineIcon style={{ fontSize: 22 }} />
        </span>
        <div className="text-left min-w-0">
          <div className="text-sm font-semibold text-[#0A0A0A]">
            Job {job.job_num}
          </div>
          <div className="text-xs text-gray-500 truncate">{job.job_name}</div>
        </div>

        <div className="ml-auto flex items-center gap-3 shrink-0">
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            <div className="text-right">
              <div className="text-lg font-bold text-[#0A0A0A] leading-none">
                {total}
              </div>
              <div className="text-[11px] text-gray-500">total forms</div>
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="text-gray-400 hover:text-red-600 p-1"
            title="Remove this job"
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
          <ExpandMoreIcon
            style={{
              fontSize: 22,
              transition: "transform .2s",
              transform: isOpen ? "rotate(180deg)" : "none",
            }}
            className="text-gray-400"
          />
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 bg-gray-100 p-3 space-y-4">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Loading analytics for job {job.job_num}…
            </div>
          ) : total === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white py-10 text-center">
              <p className="text-sm text-gray-600">
                No safety forms have been filed for this job in the selected
                range.
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-3">
                {FORM_TYPES.map((f) => (
                  <StatCard
                    key={f.code}
                    label={f.label}
                    value={data.counts[f.code] ?? 0}
                    accent={f.color}
                    Icon={f.Icon}
                    onClick={() =>
                      openDrill(
                        `${f.label} Forms — Job ${job.job_num}`,
                        data.byForm[f.code] || [],
                      )
                    }
                  />
                ))}
              </div>
              {drillNode}

              {FORM_TYPES.map((f) => {
                const formRows = data.byForm[f.code] || [];
                const Chart = f.Chart;
                return (
                  <section key={f.code} className="space-y-3">
                    <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ background: f.color }}
                      />
                      <h2
                        className="text-sm font-bold uppercase tracking-wide"
                        style={{ color: f.color }}
                      >
                        {f.label}
                      </h2>
                      <span className="text-[11px] font-semibold text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                        {formRows.length}
                      </span>
                    </div>
                    {formRows.length === 0 ? (
                      <div className="rounded-2xl border border-gray-200 bg-white py-8 text-center text-xs text-gray-400">
                        No {f.label} submissions for this job in the selected range.
                      </div>
                    ) : (
                      <Chart rows={formRows} singleJob />
                    )}
                  </section>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Merged-view charts — cross-job comparisons that exist ONLY here. The per-job
// panels keep their per-form charts; this view answers "how do the selected
// jobs compare against each other?"
// ---------------------------------------------------------------------------
const JOB_COLORS = ["#155DFC", "#00A63E", "#E17100", "#E7000B", "#7C3AED", "#0EA5E9", "#D97706", "#DC2626", "#0891B2", "#65A30D"];

const MergedCard = ({ title, subtitle, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="mb-3">
      <h2 className="text-sm font-bold text-[#0A0A0A] text-left uppercase tracking-wide">{title}</h2>
      {subtitle && <p className="text-[11px] text-[#717182] text-left">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const MergedEmpty = ({ height = 240, message = "No data in range" }) => (
  <div style={{ height }} className="flex items-center justify-center text-xs text-gray-400 text-center px-4">
    {message}
  </div>
);

const mergedRowDate = (r) => {
  if (r.created_at) return dayjs(r.created_at);
  if (r.filled_date) return dayjs(r.filled_date);
  return null;
};

const MergedCharts = ({ jobs, rowsByJob, data, openDrill }) => {
  // One stats bundle per selected job — everything below derives from this.
  const perJob = useMemo(
    () =>
      jobs.map((job, i) => {
        const key = jobKeyOf(job);
        const rows = rowsByJob[key] || [];
        const counts = Object.fromEntries(FORM_CODES.map((c) => [c, 0]));
        let safe = 0, concern = 0, miles = 0, fuelSpend = 0, shpPass = 0, shpFail = 0;
        rows.forEach((r) => {
          if (counts[r.form_type] !== undefined) counts[r.form_type] += 1;
          if (r.form_type === "BBS" && r.bbs_meta && typeof r.bbs_meta === "object") {
            Object.values(r.bbs_meta).forEach((arr) => {
              if (!Array.isArray(arr)) return;
              arr.forEach((it) => {
                if (it?.answer === "S") safe += 1;
                else if (it?.answer === "C") concern += 1;
              });
            });
          }
          if (r.form_type === "WDS" && r.wds_meta && typeof r.wds_meta === "object") {
            miles += Number(r.wds_meta.miles) || 0;
            fuelSpend += Number(r.wds_meta.amount) || 0;
          }
          if (r.form_type === "SHP" && r.shp_meta?.buckets) {
            Object.values(r.shp_meta.buckets).forEach((arr) => {
              if (!Array.isArray(arr)) return;
              arr.forEach((it) => {
                const a = (it?.answer || "").toUpperCase();
                if (a === "YES") shpPass += 1;
                else if (a === "NO") shpFail += 1;
              });
            });
          }
        });
        const obs = safe + concern;
        const audited = shpPass + shpFail;
        return {
          key,
          job,
          rows,
          name: `Job ${job.job_num}`,
          color: JOB_COLORS[i % JOB_COLORS.length],
          total: rows.length,
          ...counts,
          safe,
          concern,
          concernRate: obs ? +((concern / obs) * 100).toFixed(1) : 0,
          miles: Math.round(miles),
          fuelSpend: Math.round(fuelSpend),
          shpPass,
          shpFail,
          failRate: audited ? +((shpFail / audited) * 100).toFixed(1) : 0,
        };
      }),
    [jobs, rowsByJob],
  );

  // Monthly submissions, one series per job.
  const trendData = useMemo(() => {
    const buckets = {};
    perJob.forEach((pj) => {
      pj.rows.forEach((r) => {
        const d = mergedRowDate(r);
        if (!d) return;
        const k = d.format("YYYY-MM");
        if (!buckets[k]) buckets[k] = { key: k, label: d.format("MMM 'YY") };
        buckets[k][pj.name] = (buckets[k][pj.name] || 0) + 1;
      });
    });
    return Object.values(buckets).sort((a, b) => a.key.localeCompare(b.key));
  }, [perJob]);

  const mixData = useMemo(
    () =>
      FORM_TYPES.map((f) => ({
        code: f.code,
        name: f.label,
        value: data.counts[f.code] ?? 0,
        color: f.color,
      })).filter((d) => d.value > 0),
    [data],
  );

  const bbsJobs = perJob.filter((pj) => pj.safe + pj.concern > 0);
  const wdsJobs = perJob.filter((pj) => pj.miles > 0 || pj.fuelSpend > 0);
  const shpJobs = perJob.filter((pj) => pj.shpPass + pj.shpFail > 0);

  const allRows = useMemo(() => perJob.flatMap((pj) => pj.rows), [perJob]);

  const personOf = (r) => (r.emp_name || r.observer_name || "").trim();

  // Who files the most forms across the merged jobs.
  const topPeople = useMemo(() => {
    const m = {};
    allRows.forEach((r) => {
      const p = personOf(r);
      if (p) m[p] = (m[p] || 0) + 1;
    });
    return Object.entries(m)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [allRows]);

  // When during the week forms are filed (Mon → Sun).
  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const weekdayData = useMemo(() => {
    const counts = Object.fromEntries(WEEKDAYS.map((d) => [d, 0]));
    allRows.forEach((r) => {
      const d = mergedRowDate(r);
      if (!d) return;
      // dayjs day(): 0 = Sunday … 6 = Saturday → remap so Monday leads.
      counts[WEEKDAYS[(d.day() + 6) % 7]] += 1;
    });
    return WEEKDAYS.map((name) => ({ name, value: counts[name] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows]);

  // AIF severity split per job. Severity categories are discovered from the
  // data so new hints coming from the API just work.
  const severityOf = (r) => {
    const s = (r.severity_hint || "unclassified").toString().trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const SEVERITY_COLORS = {
    Fatal: "#7F1D1D",
    "Lost time": "#DC2626",
    Recordable: "#E7000B",
    "First aid": "#E17100",
    "Near miss": "#155DFC",
    Unclassified: "#9CA3AF",
  };
  const { severityKeys, severityByJob } = useMemo(() => {
    const keys = new Set();
    const byJob = perJob
      .map((pj) => {
        const row = { name: pj.name, __pj: pj };
        let hasAif = false;
        pj.rows.forEach((r) => {
          if (r.form_type !== "AIF") return;
          hasAif = true;
          const s = severityOf(r);
          keys.add(s);
          row[s] = (row[s] || 0) + 1;
        });
        return hasAif ? row : null;
      })
      .filter(Boolean);
    return { severityKeys: [...keys], severityByJob: byJob };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perJob]);

  const mostActive = perJob.reduce((a, b) => (b.total > (a?.total ?? -1) ? b : a), null);
  const totalObs = perJob.reduce((a, pj) => a + pj.safe + pj.concern, 0);
  const totalConcern = perJob.reduce((a, pj) => a + pj.concern, 0);
  const overallConcernRate = totalObs ? ((totalConcern / totalObs) * 100).toFixed(1) : "0.0";

  // Recharts click payloads differ per mark — normalise to the datum.
  const chartDatum = (d) => d?.payload ?? d;

  const drillJobForm = (clicked, code) => {
    const pj = chartDatum(clicked);
    if (!pj?.rows) return;
    const label = FORM_TYPES.find((f) => f.code === code)?.label || code;
    openDrill(`${label} Forms — ${pj.name}`, pj.rows.filter((r) => r.form_type === code));
  };

  // Radar fingerprint — each form type is an axis, each job a polygon. Values
  // are normalised per axis (100 = the busiest job on that form type) so jobs
  // of very different sizes stay comparable.
  const radarData = useMemo(
    () =>
      FORM_TYPES.map((f) => {
        const max = Math.max(...perJob.map((pj) => pj[f.code] || 0), 0);
        const axis = { metric: f.label };
        perJob.forEach((pj) => {
          axis[pj.name] = max ? Math.round(((pj[f.code] || 0) / max) * 100) : 0;
          axis[`${pj.name}__raw`] = pj[f.code] || 0;
        });
        return axis;
      }),
    [perJob],
  );

  // Side-by-side bars — form types on the X axis, one bar per job.
  const groupedData = useMemo(
    () =>
      FORM_TYPES.map((f) => {
        const row = { code: f.code, name: f.label };
        perJob.forEach((pj) => {
          row[pj.name] = pj[f.code] || 0;
        });
        return row;
      }),
    [perJob],
  );

  return (
    <div className="space-y-4">
      {/* KPI strip — the merged headline numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-left">Jobs Compared</div>
          <div className="mt-1 text-3xl font-bold text-[#155DFC] text-left">{jobs.length}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-left">Total Forms</div>
          <div className="mt-1 text-3xl font-bold text-[#0A0A0A] text-left">{data.total}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-left">Most Active Job</div>
          <div className="mt-1 text-3xl font-bold text-[#00A63E] text-left">
            {mostActive ? mostActive.job.job_num : "—"}
          </div>
          {mostActive && (
            <div className="text-[10px] text-gray-500 text-left">{mostActive.total} forms</div>
          )}
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-left">BBS Concern Rate</div>
          <div className="mt-1 text-3xl font-bold text-[#DC2626] text-left">{overallConcernRate}%</div>
          <div className="text-[10px] text-gray-500 text-left">across all selected jobs</div>
        </div>
      </div>

      {/* ---- Head-to-head comparison section ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Radar — the "shape" of each job's activity, overlaid */}
        <MergedCard
          title="Job Fingerprint (Radar)"
          subtitle="Each job's activity shape across the five form types — 100 = the busiest job on that axis"
        >
          {perJob.length === 0 || data.total === 0 ? (
            <MergedEmpty />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                <Tooltip
                  formatter={(v, name, ctx) => {
                    const raw = ctx?.payload?.[`${name}__raw`];
                    return raw !== undefined ? `${raw} forms (${v}/100)` : v;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {perJob.map((pj) => (
                  <Radar
                    key={pj.key}
                    name={pj.name}
                    dataKey={pj.name}
                    stroke={pj.color}
                    fill={pj.color}
                    fillOpacity={0.12}
                    strokeWidth={2}
                  />
                ))}
              </RadarChart>
            </ResponsiveContainer>
          )}
        </MergedCard>

        {/* Grouped bars — jobs side by side per form type */}
        <MergedCard
          title="Side-by-Side by Form Type"
          subtitle="Jobs compared directly on each form type — click a bar to open those forms"
        >
          {perJob.length === 0 || data.total === 0 ? (
            <MergedEmpty />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={groupedData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {perJob.map((pj) => (
                  <Bar
                    key={pj.key}
                    dataKey={pj.name}
                    fill={pj.color}
                    cursor="pointer"
                    onClick={(clicked) => {
                      const d = chartDatum(clicked);
                      if (d?.code) drillJobForm(pj, d.code);
                    }}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </MergedCard>
      </div>

      {/* Ranked comparison table — every job's key numbers side by side */}
      <MergedCard
        title="Job Comparison Table"
        subtitle="Best value per column in green, worst in red (rates: lower is better)"
      >
        {perJob.length === 0 ? (
          <MergedEmpty height={120} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-2 py-1.5">Job</th>
                  <th className="px-2 py-1.5 text-right">Total</th>
                  {FORM_TYPES.map((f) => (
                    <th key={f.code} className="px-2 py-1.5 text-right" style={{ color: f.color }}>
                      {f.label}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-right">Concern %</th>
                  <th className="px-2 py-1.5 text-right">Audit Fail %</th>
                  <th className="px-2 py-1.5 text-right">Miles</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const ranked = [...perJob].sort((a, b) => b.total - a.total);
                  // Column extremes for highlighting. Counts: high = green.
                  // Rates: low = green (computed only over jobs with data).
                  const hi = (get) => Math.max(...ranked.map(get));
                  const cellCls = (v, best, worst) =>
                    v === best && best !== worst
                      ? "text-green-700 font-bold"
                      : v === worst && best !== worst
                        ? "text-red-600 font-bold"
                        : "text-gray-700";
                  const totalHi = hi((p) => p.total);
                  const totalLo = Math.min(...ranked.map((p) => p.total));
                  const rateJobs = (fld, gate) => ranked.filter((p) => gate(p));
                  const concernJobs = rateJobs("concernRate", (p) => p.safe + p.concern > 0);
                  const concernLo = concernJobs.length ? Math.min(...concernJobs.map((p) => p.concernRate)) : null;
                  const concernHi = concernJobs.length ? Math.max(...concernJobs.map((p) => p.concernRate)) : null;
                  const failJobs = rateJobs("failRate", (p) => p.shpPass + p.shpFail > 0);
                  const failLo = failJobs.length ? Math.min(...failJobs.map((p) => p.failRate)) : null;
                  const failHi = failJobs.length ? Math.max(...failJobs.map((p) => p.failRate)) : null;
                  return ranked.map((pj) => (
                    <tr key={pj.key} className="border-t">
                      <td className="px-2 py-1.5">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full mr-1.5"
                          style={{ background: pj.color }}
                        />
                        <span className="font-semibold text-[#0A0A0A]">{pj.name}</span>
                        <span className="text-gray-400 ml-1 hidden md:inline">
                          {pj.job.job_name}
                        </span>
                      </td>
                      <td className={`px-2 py-1.5 text-right ${cellCls(pj.total, totalHi, totalLo)}`}>
                        {pj.total}
                      </td>
                      {FORM_TYPES.map((f) => (
                        <td key={f.code} className="px-2 py-1.5 text-right text-gray-700">
                          {pj[f.code] || 0}
                        </td>
                      ))}
                      <td
                        className={`px-2 py-1.5 text-right ${
                          pj.safe + pj.concern > 0
                            ? cellCls(pj.concernRate, concernLo, concernHi)
                            : "text-gray-300"
                        }`}
                      >
                        {pj.safe + pj.concern > 0 ? `${pj.concernRate}%` : "—"}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right ${
                          pj.shpPass + pj.shpFail > 0
                            ? cellCls(pj.failRate, failLo, failHi)
                            : "text-gray-300"
                        }`}
                      >
                        {pj.shpPass + pj.shpFail > 0 ? `${pj.failRate}%` : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right text-gray-700">
                        {pj.miles ? pj.miles.toLocaleString() : "—"}
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </MergedCard>

      {/* Job × form-type composition — who filed what */}
      <MergedCard
        title="Forms by Job & Type"
        subtitle="Each bar is a job; segments show its form mix. Click a segment to open those forms."
      >
        {perJob.length === 0 ? (
          <MergedEmpty />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, perJob.length * 52)}>
            <BarChart data={perJob} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" width={78} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {FORM_TYPES.map((f) => (
                <Bar
                  key={f.code}
                  dataKey={f.code}
                  name={f.label}
                  stackId="forms"
                  fill={f.color}
                  cursor="pointer"
                  onClick={(entry) => entry && drillJobForm(entry, f.code)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </MergedCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Head-to-head monthly race */}
        <MergedCard
          title="Submissions Over Time by Job"
          subtitle="Monthly form volume, one line per job"
        >
          {trendData.length === 0 ? (
            <MergedEmpty />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {perJob.map((pj) => (
                  <Line
                    key={pj.key}
                    type="monotone"
                    dataKey={pj.name}
                    stroke={pj.color}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </MergedCard>

        {/* Overall composition of the merged set */}
        <MergedCard
          title="Form Type Mix"
          subtitle="Share of each form type across all selected jobs — click a slice to open those forms"
        >
          {mixData.length === 0 ? (
            <MergedEmpty />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={mixData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  cursor="pointer"
                  onClick={(clicked) => {
                    const d = chartDatum(clicked);
                    if (!d?.code) return;
                    openDrill(
                      `${d.name} Forms — ${jobs.length} Jobs Merged`,
                      data.byForm[d.code] || [],
                    );
                  }}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {mixData.map((d) => (
                    <Cell key={d.code} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </MergedCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Safety head-to-head from BBS observations */}
        <MergedCard
          title="BBS Safety Comparison"
          subtitle="Safe vs concern observations per job, with the concern-rate line"
        >
          {bbsJobs.length === 0 ? (
            <MergedEmpty message="No BBS observations for the selected jobs" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={bbsJobs}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="obs" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis
                  yAxisId="rate"
                  orientation="right"
                  unit="%"
                  tick={{ fontSize: 10 }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  yAxisId="obs"
                  dataKey="safe"
                  name="Safe"
                  stackId="bbs"
                  fill="#16A34A"
                  cursor="pointer"
                  onClick={(entry) => entry && drillJobForm(entry, "BBS")}
                />
                <Bar
                  yAxisId="obs"
                  dataKey="concern"
                  name="Concern"
                  stackId="bbs"
                  fill="#DC2626"
                  cursor="pointer"
                  onClick={(entry) => entry && drillJobForm(entry, "BBS")}
                />
                <Line
                  yAxisId="rate"
                  type="monotone"
                  dataKey="concernRate"
                  name="Concern Rate %"
                  stroke="#D97706"
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </MergedCard>

        {/* Driver-log economics per job */}
        <MergedCard
          title="Driver Log — Miles & Fuel Spend by Job"
          subtitle="Totals from each job's weekly driver logs"
        >
          {wdsJobs.length === 0 ? (
            <MergedEmpty message="No driver-log mileage for the selected jobs" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={wdsJobs}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="miles" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis
                  yAxisId="spend"
                  orientation="right"
                  tickFormatter={(v) => `$${v}`}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip formatter={(v, n) => (n === "Fuel Spend" ? `$${Number(v).toLocaleString()}` : Number(v).toLocaleString())} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  yAxisId="miles"
                  dataKey="miles"
                  name="Miles"
                  fill="#E17100"
                  cursor="pointer"
                  onClick={(entry) => entry && drillJobForm(entry, "WDS")}
                />
                <Line
                  yAxisId="spend"
                  type="monotone"
                  dataKey="fuelSpend"
                  name="Fuel Spend"
                  stroke="#0A0A0A"
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </MergedCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Who does the reporting across the merged jobs */}
        <MergedCard
          title="Top Contributors"
          subtitle="People filing the most forms across the selected jobs — click a bar to open their forms"
        >
          {topPeople.length === 0 ? (
            <MergedEmpty />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, topPeople.length * 32)}>
              <BarChart data={topPeople} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip />
                <Bar
                  dataKey="value"
                  name="Forms"
                  fill="#155DFC"
                  cursor="pointer"
                  onClick={(clicked) => {
                    const d = chartDatum(clicked);
                    if (!d?.name) return;
                    openDrill(
                      `Forms filed by ${d.name}`,
                      allRows.filter((r) => personOf(r) === d.name),
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </MergedCard>

        {/* Weekly rhythm of the field work */}
        <MergedCard
          title="Activity by Day of Week"
          subtitle="When forms get filed across the selected jobs — click a day to open its forms"
        >
          {allRows.length === 0 ? (
            <MergedEmpty />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={weekdayData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar
                  dataKey="value"
                  name="Forms"
                  fill="#7C3AED"
                  cursor="pointer"
                  onClick={(clicked) => {
                    const d = chartDatum(clicked);
                    if (!d?.name) return;
                    openDrill(
                      `Forms filed on ${d.name}`,
                      allRows.filter((r) => {
                        const dt = mergedRowDate(r);
                        return dt && WEEKDAYS[(dt.day() + 6) % 7] === d.name;
                      }),
                    );
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </MergedCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Audit quality head-to-head from SHP checklists */}
        <MergedCard
          title="Audit Pass / Fail by Job"
          subtitle="Safety & Health checklist answers per job, with the fail-rate line"
        >
          {shpJobs.length === 0 ? (
            <MergedEmpty message="No Safety & Health audit items for the selected jobs" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={shpJobs}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="items" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis
                  yAxisId="rate"
                  orientation="right"
                  unit="%"
                  tick={{ fontSize: 10 }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  yAxisId="items"
                  dataKey="shpPass"
                  name="Pass"
                  stackId="shp"
                  fill="#16A34A"
                  cursor="pointer"
                  onClick={(entry) => entry && drillJobForm(entry, "SHP")}
                />
                <Bar
                  yAxisId="items"
                  dataKey="shpFail"
                  name="Fail"
                  stackId="shp"
                  fill="#DC2626"
                  cursor="pointer"
                  onClick={(entry) => entry && drillJobForm(entry, "SHP")}
                />
                <Line
                  yAxisId="rate"
                  type="monotone"
                  dataKey="failRate"
                  name="Fail Rate %"
                  stroke="#D97706"
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </MergedCard>

        {/* Incident severity split per job */}
        <MergedCard
          title="Incident Severity by Job"
          subtitle="Accident/Incident forms per job, stacked by severity — click to open that job's incidents"
        >
          {severityByJob.length === 0 ? (
            <MergedEmpty message="No Accident/Incident forms for the selected jobs" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={severityByJob}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {severityKeys.map((sk, i) => (
                  <Bar
                    key={sk}
                    dataKey={sk}
                    name={sk}
                    stackId="sev"
                    fill={SEVERITY_COLORS[sk] || JOB_COLORS[i % JOB_COLORS.length]}
                    cursor="pointer"
                    onClick={(clicked) => {
                      const d = chartDatum(clicked);
                      if (d?.__pj) drillJobForm(d.__pj, "AIF");
                    }}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </MergedCard>
      </div>
    </div>
  );
};

// Merged panel — every selected job's rows combined into one view, rendered
// with the merged-only comparison charts above.
const MergedPanel = ({ jobs, rowsByJob, data, loading, isOpen, onToggle }) => {
  const total = data?.total ?? 0;
  const { openDrill, node: drillNode } = useDrill([]);
  return (
    <div className="rounded-2xl border-2 border-[#155DFC] bg-white shadow-sm overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className={`flex items-center gap-3 p-4 cursor-pointer transition ${
          isOpen ? "bg-blue-50/60" : "hover:bg-gray-50"
        }`}
      >
        <span className="rounded-full bg-blue-50 p-2 text-[#155DFC] shrink-0">
          <CallMergeIcon style={{ fontSize: 22 }} />
        </span>
        <div className="text-left min-w-0">
          <div className="text-sm font-semibold text-[#0A0A0A]">
            All Selected Jobs — Merged
          </div>
          <div className="text-xs text-gray-500 truncate">
            {jobs.map((j) => j.job_num).join(", ")}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3 shrink-0">
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            <div className="text-right">
              <div className="text-lg font-bold text-[#0A0A0A] leading-none">
                {total}
              </div>
              <div className="text-[11px] text-gray-500">total forms</div>
            </div>
          )}
          <ExpandMoreIcon
            style={{
              fontSize: 22,
              transition: "transform .2s",
              transform: isOpen ? "rotate(180deg)" : "none",
            }}
            className="text-gray-400"
          />
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 bg-gray-100 p-3 space-y-4">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Loading analytics for the selected jobs…
            </div>
          ) : total === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white py-10 text-center">
              <p className="text-sm text-gray-600">
                No safety forms have been filed for the selected jobs in the
                selected range.
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-3">
                {FORM_TYPES.map((f) => (
                  <StatCard
                    key={f.code}
                    label={f.label}
                    value={data.counts[f.code] ?? 0}
                    accent={f.color}
                    Icon={f.Icon}
                    onClick={() =>
                      openDrill(
                        `${f.label} Forms — ${jobs.length} Jobs Merged`,
                        data.byForm[f.code] || [],
                      )
                    }
                  />
                ))}
              </div>
              {drillNode}

              <MergedCharts
                jobs={jobs}
                rowsByJob={rowsByJob}
                data={data}
                openDrill={openDrill}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

const JobAnalytics = () => {
  const userDetails = useSelector((state) => state.user.userDetails);

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);

  // Multi-select: every chosen job gets its own feed and its own panel.
  const [selectedJobs, setSelectedJobs] = useState([]); // [{ job_num, job_name }]
  const [rowsByJob, setRowsByJob] = useState({}); // job_num -> rows[]
  const [loadingJobs, setLoadingJobs] = useState({}); // job_num -> bool
  const [expanded, setExpanded] = useState(null); // job_num of the open panel

  const [loader, setLoader] = useState(false);
  const [rangeKey, setRangeKey] = useState("all");

  // Merge view — one combined panel over every selected job, shown above the
  // per-job list. Only meaningful with 2+ jobs selected.
  const [mergeView, setMergeView] = useState(false);
  const [mergedOpen, setMergedOpen] = useState(true);

  // "Browse all jobs" dropdown — the whole company roster from /v1/jobs/list,
  // so jobs can be picked without knowing a number to search for.
  const [allJobs, setAllJobs] = useState([]);
  const [loadingAllJobs, setLoadingAllJobs] = useState(false);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [rosterFilter, setRosterFilter] = useState("");

  const searchRef = useRef(null);
  const rosterRef = useRef(null);

  const pmId = userDetails?.role === 3 ? userDetails.id : null;

  // Deep link from the Safety Analytics job roster:
  // /job-analytics?company=<id>&job=<num>&name=<job name>
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const company = p.get("company");
    const job = p.get("job");
    if (!job) return;
    if (company) setCompanyId(company);
    const incoming = { job_num: job, job_name: p.get("name") || "" };
    setSelectedJobs([incoming]);
    setExpanded(jobKeyOf(incoming));
  }, []);

  // Load the company list (jobs are company-scoped in the external API).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/v1/timesheets/company-details");
        if (!cancelled && resp?.status === 200 && Array.isArray(resp.data)) {
          setCompanies(resp.data);
        }
      } catch (e) {
        console.error("Failed to load companies", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the suggestions / roster dropdowns when clicking outside them.
  useEffect(() => {
    const onClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
      if (rosterRef.current && !rosterRef.current.contains(e.target)) {
        setShowAllJobs(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Pull the company's full job roster once per company so the "All jobs"
  // dropdown can offer every job without a search term.
  useEffect(() => {
    setAllJobs([]);
    setRosterFilter("");
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingAllJobs(true);
        const resp = await api.get("/v1/jobs/list", {
          params: { companyid: companyId, status: "all" },
        });
        if (!cancelled && resp?.status === 200 && Array.isArray(resp.data)) {
          setAllJobs(resp.data);
        }
      } catch (e) {
        console.error("Failed to load job roster", e);
      } finally {
        if (!cancelled) setLoadingAllJobs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Debounced job autocomplete against the company-scoped jobs endpoint.
  useEffect(() => {
    const q = query.trim();
    if (!q || !companyId) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const resp = await api.get("/v1/timesheets/get-jobs", {
          params: { company_id: companyId, job_num: q },
        });
        if (!cancelled) {
          setSuggestions(resp?.status === 200 && Array.isArray(resp.data) ? resp.data : []);
          setShowSuggestions(true);
        }
      } catch (e) {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, companyId]);

  const resolveRange = () => {
    if (rangeKey === "ytd") return { from: dayjs().startOf("year"), to: dayjs() };
    if (rangeKey === "6m") return { from: dayjs().subtract(6, "month"), to: dayjs() };
    if (rangeKey === "90d") return { from: dayjs().subtract(90, "day"), to: dayjs() };
    if (rangeKey === "30d") return { from: dayjs().subtract(30, "day"), to: dayjs() };
    // "all": a window wide enough to capture every form filed for the job.
    return { from: dayjs("2000-01-01"), to: dayjs() };
  };

  // The analytics endpoint scopes to ONE job, so fan out across the selection
  // and keep each job's rows in its own bucket.
  useEffect(() => {
    if (!companyId || !userDetails || selectedJobs.length === 0) {
      setRowsByJob({});
      setLoadingJobs({});
      return;
    }
    let cancelled = false;
    const { from, to } = resolveRange();
    const baseParams = {
      from: from.format("YYYY-MM-DD"),
      to: to.format("YYYY-MM-DD"),
      form_types: FORM_CODES.join(","),
      companyid: companyId,
    };
    if (pmId != null) baseParams.pm_id = pmId;

    (async () => {
      setLoader(true);
      setLoadingJobs(
        Object.fromEntries(selectedJobs.map((j) => [jobKeyOf(j), true])),
      );
      const results = await Promise.all(
        selectedJobs.map(async (job) => {
          const key = jobKeyOf(job);
          try {
            const resp = await api.get("/v1/forms/analytics", {
              params: { ...baseParams, job_id: job.job_num },
            });
            return [
              key,
              resp?.status === 200 && Array.isArray(resp.data) ? resp.data : [],
            ];
          } catch (err) {
            console.error(`Job analytics fetch failed for ${key}`, err);
            return [key, []];
          }
        }),
      );
      if (cancelled) return;
      setRowsByJob(Object.fromEntries(results));
      setLoadingJobs({});
      setLoader(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobs, companyId, rangeKey, userDetails]);

  const handleSelectJob = (job) => {
    const key = jobKeyOf(job);
    setSelectedJobs((prev) =>
      prev.some((j) => jobKeyOf(j) === key) ? prev : [...prev, job],
    );
    // Open whatever was just added so the user sees it immediately.
    setExpanded(key);
    setQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Checkbox toggle from the roster dropdown — add if missing, remove if not.
  const toggleJob = (job) => {
    const key = jobKeyOf(job);
    setSelectedJobs((prev) => {
      if (prev.some((j) => jobKeyOf(j) === key)) {
        setExpanded((cur) => (cur === key ? null : cur));
        return prev.filter((j) => jobKeyOf(j) !== key);
      }
      setExpanded(key);
      return [...prev, { job_num: job.job_num, job_name: job.job_name }];
    });
  };

  const removeJob = (key) => {
    setSelectedJobs((prev) => prev.filter((j) => jobKeyOf(j) !== key));
    setExpanded((cur) => (cur === key ? null : cur));
  };

  const clearAll = () => {
    setSelectedJobs([]);
    setRowsByJob({});
    setExpanded(null);
    setQuery("");
    setSuggestions([]);
  };

  const handleCompanyChange = (e) => {
    setCompanyId(e.target.value);
    // Company drives which jobs exist — reset the whole selection.
    clearAll();
  };

  const alreadyAdded = (job) =>
    selectedJobs.some((j) => jobKeyOf(j) === jobKeyOf(job));

  // Per-job form buckets + counts, derived once per feed change.
  const derived = useMemo(() => {
    const out = {};
    Object.entries(rowsByJob).forEach(([key, rows]) => {
      const counts = Object.fromEntries(FORM_CODES.map((c) => [c, 0]));
      const byForm = Object.fromEntries(FORM_CODES.map((c) => [c, []]));
      rows.forEach((r) => {
        if (byForm[r.form_type]) {
          byForm[r.form_type].push(r);
          counts[r.form_type] += 1;
        }
      });
      out[key] = { counts, byForm, total: rows.length };
    });
    return out;
  }, [rowsByJob]);

  const grandTotal = useMemo(
    () => Object.values(derived).reduce((a, d) => a + d.total, 0),
    [derived],
  );

  // Every selected job's rows in one bucket, for the merged panel.
  const mergedData = useMemo(() => {
    const rows = Object.values(rowsByJob).flat();
    const counts = Object.fromEntries(FORM_CODES.map((c) => [c, 0]));
    const byForm = Object.fromEntries(FORM_CODES.map((c) => [c, []]));
    rows.forEach((r) => {
      if (byForm[r.form_type]) {
        byForm[r.form_type].push(r);
        counts[r.form_type] += 1;
      }
    });
    return { counts, byForm, total: rows.length };
  }, [rowsByJob]);

  const anyJobLoading = Object.values(loadingJobs).some(Boolean);

  return (
    <SideNavLayout>
      <div
        className="p-2 max-h-[90vh] overflow-y-auto space-y-4"
        style={{ scrollbarWidth: "none" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="text-left min-w-0">
            <h1 className="text-xl font-semibold text-[#0A0A0A]">Job Analytics</h1>
            <p className="text-sm text-[#717182] max-w-2xl">
              Pick a company and search one or more jobs to see analytics across
              all five safety forms — BBS, Safety &amp; Health, Driver Log,
              Accident/Incident and Trailer.
            </p>
          </div>

          {/* Single row — the pills must never wrap mid-group. On narrow
              screens the strip scrolls sideways instead of breaking. */}
          {selectedJobs.length > 0 && (
            <div className="inline-flex flex-nowrap rounded-lg border border-gray-200 bg-white p-1 shadow-sm self-start max-w-full overflow-x-auto">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRangeKey(r.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition whitespace-nowrap shrink-0 ${
                    rangeKey === r.key
                      ? "bg-[#0A0A0A] text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Company + Job search */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Company selector */}
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm sm:w-96 shrink-0">
            <BusinessIcon style={{ fontSize: 20 }} className="text-gray-400 shrink-0" />
            <select
              value={companyId}
              onChange={handleCompanyChange}
              title={companies.find((c) => String(c.id) === String(companyId))?.name || ""}
              // min-w-0 lets the select shrink inside the flex row, and
              // text-overflow keeps long company names from being clipped
              // mid-word (native selects don't truncate on their own).
              className="flex-1 min-w-0 text-sm outline-none bg-transparent truncate"
              style={{ textOverflow: "ellipsis" }}
            >
              <option value="">Select company…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* All-jobs dropdown — browse the whole roster, tick one or many */}
          <div className="relative shrink-0" ref={rosterRef}>
            <button
              type="button"
              disabled={!companyId}
              onClick={() => setShowAllJobs((v) => !v)}
              className={`flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm text-sm h-full transition ${
                companyId
                  ? "hover:bg-gray-50 text-gray-700"
                  : "opacity-60 cursor-not-allowed text-gray-400"
              }`}
            >
              <WorkOutlineIcon style={{ fontSize: 18 }} className="text-gray-400" />
              <span className="whitespace-nowrap">
                {loadingAllJobs
                  ? "Loading jobs…"
                  : `All jobs${allJobs.length ? ` (${allJobs.length})` : ""}`}
              </span>
              <ExpandMoreIcon
                style={{
                  fontSize: 18,
                  transition: "transform .2s",
                  transform: showAllJobs ? "rotate(180deg)" : "none",
                }}
                className="text-gray-400"
              />
            </button>

            {showAllJobs && companyId && (
              <div className="absolute z-50 mt-1 w-80 rounded-xl border border-gray-200 bg-white shadow-lg">
                {/* Local filter over the already-loaded roster */}
                <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
                  <SearchIcon style={{ fontSize: 16 }} className="text-gray-400" />
                  <input
                    value={rosterFilter}
                    onChange={(e) => setRosterFilter(e.target.value)}
                    placeholder="Filter jobs…"
                    className="flex-1 text-xs outline-none bg-transparent"
                  />
                  {rosterFilter && (
                    <button
                      onClick={() => setRosterFilter("")}
                      className="text-gray-400 hover:text-gray-600"
                      title="Clear filter"
                    >
                      <CloseIcon style={{ fontSize: 14 }} />
                    </button>
                  )}
                </div>

                <ul className="max-h-72 overflow-y-auto py-1">
                  {loadingAllJobs ? (
                    <li className="px-3 py-4 text-center text-xs text-gray-400">
                      Loading job roster…
                    </li>
                  ) : (
                    (() => {
                      const needle = rosterFilter.trim().toLowerCase();
                      const visible = needle
                        ? allJobs.filter(
                            (j) =>
                              String(j.job_num).toLowerCase().includes(needle) ||
                              String(j.job_name || "").toLowerCase().includes(needle),
                          )
                        : allJobs;
                      if (visible.length === 0) {
                        return (
                          <li className="px-3 py-4 text-center text-xs text-gray-400">
                            {allJobs.length === 0
                              ? "No jobs found for this company."
                              : "No jobs match the filter."}
                          </li>
                        );
                      }
                      return visible.map((job, i) => {
                        const checked = alreadyAdded(job);
                        return (
                          <li
                            key={`${job.job_num}-${i}`}
                            onClick={() => toggleJob(job)}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-left cursor-pointer hover:bg-blue-50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              readOnly
                              className="shrink-0 pointer-events-none"
                            />
                            <span className="font-medium text-gray-800 shrink-0">
                              {job.job_num}
                            </span>
                            <span className="text-gray-500 truncate" title={job.job_name || ""}>
                              — {job.job_name || "Unnamed job"}
                            </span>
                            {Number(job.forms_count) > 0 && (
                              <span className="ml-auto shrink-0 text-[10px] font-semibold text-gray-500 bg-gray-100 rounded-full px-1.5 py-0.5">
                                {job.forms_count}
                              </span>
                            )}
                          </li>
                        );
                      });
                    })()
                  )}
                </ul>

                <div className="border-t border-gray-100 px-3 py-1.5 text-[10px] text-gray-400 text-left">
                  Tick jobs to add or remove them. Badge = forms filed.
                </div>
              </div>
            )}
          </div>

          {/* Job search — adds to the selection rather than replacing it */}
          <div className="relative flex-1 max-w-xl" ref={searchRef}>
            <div
              className={`flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-200 ${
                !companyId ? "opacity-60" : ""
              }`}
            >
              <SearchIcon style={{ fontSize: 20 }} className="text-gray-400" />
              <input
                value={query}
                disabled={!companyId}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => suggestions.length && setShowSuggestions(true)}
                placeholder={
                  companyId
                    ? "Search a job number or name to add…"
                    : "Select a company first…"
                }
                className="flex-1 text-sm outline-none bg-transparent disabled:cursor-not-allowed"
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery("");
                    setSuggestions([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <CloseIcon style={{ fontSize: 18 }} />
                </button>
              )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                {suggestions.map((job, i) => {
                  const added = alreadyAdded(job);
                  return (
                    <li
                      key={`${job.job_num}-${i}`}
                      onClick={() => !added && handleSelectJob(job)}
                      className={`flex items-center gap-2 px-3 py-2 text-sm text-left ${
                        added
                          ? "cursor-default text-gray-400"
                          : "cursor-pointer hover:bg-blue-50"
                      }`}
                    >
                      <WorkOutlineIcon style={{ fontSize: 16 }} className="text-gray-400" />
                      <span className={`font-medium ${added ? "" : "text-gray-800"}`}>
                        {job.job_num}
                      </span>
                      <span className="text-gray-500 truncate">— {job.job_name}</span>
                      {added && (
                        <CheckCircleIcon
                          style={{ fontSize: 15 }}
                          className="ml-auto text-green-600 shrink-0"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {showSuggestions && !searching && companyId && query.trim() && suggestions.length === 0 && (
              <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-lg">
                No matching jobs found.
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        {selectedJobs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 text-center">
            <WorkOutlineIcon style={{ fontSize: 40 }} className="text-gray-300 mb-2" />
            <p className="text-sm text-gray-600">
              {companyId
                ? "Search and select one or more jobs above to view their analytics across all five safety forms."
                : "Select a company, then search and select jobs to view their analytics."}
            </p>
          </div>
        ) : (
          <>
            {/* Selection summary */}
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>
                <b className="text-[#0A0A0A]">{selectedJobs.length}</b>{" "}
                {selectedJobs.length === 1 ? "job" : "jobs"} selected ·{" "}
                <b className="text-[#0A0A0A]">{grandTotal}</b> total forms
              </span>
              {selectedJobs.length > 1 && (
                <button
                  onClick={() => {
                    setMergeView((v) => !v);
                    setMergedOpen(true);
                  }}
                  className={`ml-auto flex items-center gap-1 px-3 py-1 rounded-md border transition ${
                    mergeView
                      ? "bg-[#155DFC] border-[#155DFC] text-white hover:bg-blue-700"
                      : "border-gray-200 hover:bg-gray-100 text-gray-600"
                  }`}
                  title={
                    mergeView
                      ? "Remove the merged panel"
                      : "Combine all selected jobs into one panel at the top"
                  }
                >
                  <CallMergeIcon style={{ fontSize: 15 }} />
                  {mergeView ? "Unmerge" : "Merge"}
                </button>
              )}
              <button
                onClick={clearAll}
                className={`${
                  selectedJobs.length > 1 ? "" : "ml-auto"
                } px-3 py-1 rounded-md border border-gray-200 hover:bg-gray-100 text-gray-600`}
              >
                Clear all
              </button>
            </div>

            {/* Merged panel — all selected jobs combined, pinned on top */}
            {mergeView && selectedJobs.length > 1 && (
              <MergedPanel
                jobs={selectedJobs}
                rowsByJob={rowsByJob}
                data={mergedData}
                loading={anyJobLoading}
                isOpen={mergedOpen}
                onToggle={() => setMergedOpen((v) => !v)}
              />
            )}

            {/* One collapsible panel per job — accordion, one open at a time */}
            <div className="space-y-3">
              {selectedJobs.map((job) => {
                const key = jobKeyOf(job);
                return (
                  <JobPanel
                    key={key}
                    job={job}
                    data={derived[key]}
                    loading={!!loadingJobs[key]}
                    isOpen={expanded === key}
                    onToggle={() => setExpanded((cur) => (cur === key ? null : key))}
                    onRemove={() => removeJob(key)}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </SideNavLayout>
  );
};

export default JobAnalytics;
